<template>
  <div>
    <!-- Filtros Básicos Sempre Visíveis -->
    <v-card flat class="mb-4">
      <v-card-text class="pb-2">
        <v-row>
          <v-col md="3">
            <v-text-field
              hide-details="auto"
              type="date"
              :dense="false"
              solo
              flat
              v-model="searchParams.due_date_start"
              background-color="grey lighten-4"
              label="Data Inicial"
              @change="select(1)"
            />
          </v-col>
          <v-col md="3">
            <v-text-field
              background-color="grey lighten-4"
              hide-details="auto"
              type="date"
              solo
              :dense="false"
              flat
              v-model="searchParams.due_date_end"
              label="Data Final"
              @change="select(1)"
            />
          </v-col>
          <v-col md="4">
            <app-text-search-field
              label="Pesquisar por produtos"
              @input="handleSearchInput($event)"
              @filter="showAdvancedFilters = !showAdvancedFilters"
              :loading="isLoading"
            />
          </v-col>

          <v-col md="2">
            <v-menu
              v-model="columnMenu"
              :close-on-content-click="false"
              min-width="400"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  block
                  elevation="0"
                  class="pa-0 ma-0"
                  style="box-shadow: none !important; min-height: 46px;"
                >
                  Colunas
                  <v-icon right color="secondary">mdi-view-column</v-icon>
                </v-btn>
              </template>

              <v-card flat>
                <v-card-text
                  class="pa-0"
                  style="max-height: 400px; overflow-y: auto;"
                >
                  <v-list dense>
                    <v-list-item-group multiple>
                      <v-list-item
                        v-for="column in columnOptions"
                        :key="column.value"
                        @click="toggleColumn(column.value)"
                        class="px-4"
                      >
                        <v-list-item-action>
                          <v-checkbox
                            :input-value="visibleColumns.includes(column.value)"
                            color="primary"
                            hide-details
                            @click.stop="toggleColumn(column.value)"
                          />
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title class="text-body-2">
                            {{ column.text }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list-item-group>
                  </v-list>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="pa-3">
                  <v-btn small color="primary" @click="selectAllColumns">
                    <v-icon left small>mdi-check-all</v-icon>
                  </v-btn>
                  <v-btn small outlined color="error" @click="clearAllColumns">
                    <v-icon left small>mdi-close-box-multiple</v-icon>
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn small color="primary" @click="resetDefaultColumns">
                    <v-icon left small>mdi-refresh</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Filtros Avançados - Ocultos por padrão -->
    <v-expand-transition>
      <v-card
        v-show="showAdvancedFilters"
        flat
        class="mb-4 grey lighten-4 pa-2"
      >
        <v-card-text>
          <v-row>
            <v-col md="4">
              <company-select
                chips
                clearable
                v-model="searchParams.company_id"
                :item-value="(val) => val.id"
                :itemText="(val) => val.short_name"
                dense
                @change="select(1)"
              />
            </v-col>
            <v-col md="4">
              <CustomerSearch clearable dense @input="setCustomer($event)" />
            </v-col>
            <v-col md="4">
              <SalesmanSearch clearable dense @input="setSalesman($event)" />
            </v-col>
          </v-row>

          <v-row>
            <v-col md="4">
              <CategoriesSearch
                dense
                @input="handleRelationshipInput($event, 'category_id')"
              />
            </v-col>
            <v-col md="4">
              <BrandsSearch
                dense
                @input="handleRelationshipInput($event, 'brand_id')"
              />
            </v-col>
            <v-col md="10">
              <div class="d-flex flex-column">
                <v-switch
                  :label="'Somente com estoque disponível'"
                  color="primary"
                  hide-details
                  v-model="searchParams.only_positive_stock"
                  @change="select(1)"
                  true-value="true"
                  :false-value="null"
                  class="mb-2"
                />
                <v-switch
                  :label="'Somente com produtos disponíveis'"
                  color="primary"
                  hide-details
                  v-model="searchParams.only_available_stock"
                  @change="select(1)"
                  true-value="true"
                  :false-value="null"
                />
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-expand-transition>
    <v-row>
      <v-col md="12">
        <v-chip-group
          v-model="searchParams.sales_status"
          multiple
          mandatory
          @change="select(1)"
        >
          <v-chip filter outlined value="sale"> Vendido </v-chip>
          <v-chip filter outlined value="order"> Pedido </v-chip>
          <v-chip filter outlined value="waiting_approval">
            Aguardando Aprovação
          </v-chip>
          <v-chip filter outlined value="proposal"> Proposta </v-chip>
          <v-chip filter outlined value="draft"> Rascunho </v-chip>
          <v-chip filter outlined value="canceled"> Cancelado </v-chip>
        </v-chip-group>
      </v-col>
    </v-row>

    <v-row class="mt-6" v-if="sales.data">
      <v-col md="12" v-for="report in sales.data" :key="report.customer">
        <v-card flat class="mb-6">
          <v-card-title
            class="d-flex justify-space-between align-center py-3 px-4 grey lighten-4"
          >
            <div class="d-flex align-center">
              <div>
                <div class="font-weight-bold text-body-1" style="color: #222;">
                  {{ report.customer_code }} | {{ report.customer }}
                </div>
                <div class="text-caption" style="color: #888;">
                  Cliente
                </div>
              </div>
            </div>
            <div class="d-flex gap-3">
              <div class="text-right mr-4">
                <div class="text-caption" style="color: #888;">Reservado</div>
                <div class="font-weight-bold text-h6">
                  {{ Number(report.total_reserved_quantity).toFixed(2) }}
                </div>
              </div>
              <div class="text-right">
                <div class="text-caption" style="color: #888;">Disponível</div>
                <div class="font-weight-bold text-h6">
                  {{ Number(report.total_available_quantity).toFixed(2) }}
                </div>
              </div>
            </div>
          </v-card-title>

          <v-card-text class="pa-0">
            <v-data-table
              :headers="filteredHeaders"
              :items="report.reservations"
              hide-default-footer
              :items-per-page="-1"
              class="table-striped grey lighten-4"
              flat
              style="background-color: #f5f5f5;"
            >
              <template v-slot:item.sale_status="{ item }">
                <v-chip
                  :color="getStatusColor(item.sale_status)"
                  dark
                  small
                  class="font-weight-bold"
                >
                  {{ statusMap[item.sale_status] || item.sale_status }}
                </v-chip>
              </template>

              <template v-slot:item.sale_number="{ item }">
                <span class="font-weight-bold">
                  {{ item.sale_number }}
                </span>
              </template>

              <template v-slot:item.product_code="{ item }">
                {{ item.product_code }}
              </template>

              <template v-slot:item.product_name="{ item }">
                <div class="text-truncate" style="max-width: 200px;">
                  <span class="font-weight-medium">{{
                    item.product_name
                  }}</span>
                </div>
              </template>

              <template #item.created_at="{ item }">
                <div class="d-flex align-center">
                  <span class="text-body-2">{{
                    formatDate(item.created_at)
                  }}</span>
                </div>
              </template>

              <template #item.created_at_hours="{ item }">
                <div class="d-flex align-center ">
                  <v-icon small color="grey" class="mr-1">mdi-clock</v-icon>
                  <span class="text-body-2 ">{{
                    formatDateHours(item.created_at)
                  }}</span>
                </div>
              </template>

              <template #item.product_available_stock="{ item }">
                <span
                  :class="
                    Number(item.product_available_stock) <= 0
                      ? 'error--text font-weight-bold'
                      : 'font-weight-bold'
                  "
                >
                  {{ Number(item.product_available_stock).toFixed(2) }}
                </span>
              </template>

              <template #item.reserved_quantity="{ item }">
                <span class="font-weight-bold">
                  {{ Number(item.reserved_quantity).toFixed(2) }}
                </span>
              </template>

              <template #item.available_quantity="{ item }">
                <span
                  class="font-weight-bold"
                  :class="
                    Number(item.available_quantity) > 0 ? 'success--text' : ''
                  "
                >
                  {{ Number(item.available_quantity).toFixed(2) }}
                </span>
              </template>

              <template #item.company_name="{ item }">
                <v-chip outlined small>
                  {{ item.company_name }}
                </v-chip>
              </template>

              <template #item.brand_name="{ item }">
                <span class="text-body-2">{{ item.brand_name }}</span>
              </template>

              <template #item.category_name="{ item }">
                <span class="text-body-2">{{ item.category_name }}</span>
              </template>

              <template #item.sales_man="{ item }">
                <div class="d-flex align-center">
                  <v-icon small color="grey" class="mr-1">mdi-account</v-icon>
                  <span class="text-body-2">{{ item.sales_man }}</span>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row class="text-center mt-16">
      <v-col cols="12" v-if="sales.data && sales.data.length <= 0">
        <p>Sem nenhum resultado para sua pesquisa...</p>
      </v-col>
    </v-row>

    <v-row v-if="sales.links">
      <v-col>
        <app-pagination @click="select($event)" :data="sales" />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import CompanySelect from "@/components/company/ui/CompanySelect.vue";
import CustomerSearch from "@/components/customer/ui/CustomerSearch.vue";
import SalesmanSearch from "@/components/employees/ui/SalesmanSearch.vue";
import BrandsSearch from "@/components/brands/ui/BrandsSearch.vue";
import CategoriesSearch from "@/components/categories/ui/CategoriesSearch.vue";

import { format, parseISO, startOfMonth } from "date-fns";

export default {
  components: {
    CompanySelect,
    CustomerSearch,
    SalesmanSearch,
    BrandsSearch,
    CategoriesSearch,
  },

  data() {
    return {
      headers: [
        { text: "N° Venda", value: "sale_number", align: "center" },
        { text: "Status", value: "sale_status", align: "start" },
        { text: "Code", value: "product_code", align: "center" },
        { text: "Descrição", value: "product_name", align: "start" },
        { text: "Empresa", value: "company_name", align: "start" },
        { text: "Marca", value: "brand_name", align: "start" },
        { text: "Categoria", value: "category_name", align: "start" },
        { text: "Vendedor", value: "sales_man", align: "start" },
        { text: "Data", value: "created_at", align: "start" },
        { text: "Horas", value: "created_at_hours", align: "start" },
        { text: "Estoque", value: "product_available_stock", align: "center" },
        { text: "Reservado", value: "reserved_quantity", align: "center" },
        { text: "Disponível", value: "available_quantity", align: "center" },
      ],

      // Opções de colunas para controle de visibilidade
      columnOptions: [
        { text: "N° Venda", value: "sale_number" },
        { text: "Status", value: "sale_status" },
        { text: "Code", value: "product_code" },
        { text: "Descrição", value: "product_name" },
        { text: "Empresa", value: "company_name" },
        { text: "Marca", value: "brand_name" },
        { text: "Categoria", value: "category_name" },
        { text: "Vendedor", value: "sales_man" },
        { text: "Data", value: "created_at" },
        { text: "Horas", value: "created_at_hours" },
        { text: "Estoque", value: "product_available_stock" },
        { text: "Reservado", value: "reserved_quantity" },
        { text: "Disponível", value: "available_quantity" },
      ],

      // Colunas visíveis por padrão (as mais essenciais)
      visibleColumns: [
        "sale_number",
        "sale_status",
        "product_code",
        "product_name",
        "company_name",
        "sales_man",
        "product_available_stock",
        "reserved_quantity",
        "available_quantity",
      ],

      // Colunas padrão para reset
      defaultColumns: [
        "sale_number",
        "sale_status",
        "product_code",
        "product_name",
        "company_name",
        "product_available_stock",
        "reserved_quantity",
        "available_quantity",
      ],

      columnMenu: false,
      showAdvancedFilters: false,

      sales: [],

      searchParams: {
        company_id: [],
        due_date_start: format(startOfMonth(new Date()), "yyyy-MM-dd"),
        due_date_end: format(new Date(), "yyyy-MM-dd"),
        search: null,
        customer_id: null,
        salesman_id: null,
        only_positive_stock: null,
        category_id: null,
        brand_id: null,
        only_available_stock: "true",
        sales_status: ["sale"],
      },

      statusMap: {
        sale: "Vendido",
        order: "Pedido",
        waiting_approval: "Aguardando Aprovação",
        proposal: "Proposta",
        draft: "Rascunho",
        canceled: "Cancelado",
      },

      isLoading: false,
    };
  },

  mounted() {
    this.select(1);
  },

  computed: {
    filteredHeaders() {
      return this.headers.filter((header) =>
        this.visibleColumns.includes(header.value)
      );
    },

    getVisibleColumnLabels() {
      return this.columnOptions
        .filter((col) => this.visibleColumns.includes(col.value))
        .map((col) => col.text);
    },
  },

  methods: {
    async select(page = 1) {
      this.searchParams.page = page;
      this.isLoading = true;

      try {
        const response = await this.$http.index(
          "/customer/reservation-report",
          this.searchParams
        );
        this.sales = response;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    handleSearchInput(search) {
      this.searchParams.search = search ? search : null;
      this.select(1);
    },

    formatDate(dateString) {
      const date = parseISO(dateString);
      return format(date, "dd/MM/yyyy");
    },
    formatDateHours(dateString) {
      const date = parseISO(dateString);
      return format(date, "H:mm");
    },

    setCustomer(customer) {
      this.selected = [];
      this.searchParams.customer_id = customer.id;
      this.select(1);
    },

    setSalesman(salesman) {
      this.searchParams.salesman_id = salesman ? salesman.id : null;
      this.select(1);
    },

    handleRelationshipInput(relationship, field) {
      if (relationship) {
        this.searchParams[field] = relationship.id;
      } else {
        this.searchParams[field] = null;
      }

      this.select(1);
    },

    // Métodos para controle de colunas
    toggleColumn(columnValue) {
      const index = this.visibleColumns.indexOf(columnValue);
      if (index > -1) {
        this.visibleColumns.splice(index, 1);
      } else {
        this.visibleColumns.push(columnValue);
      }
    },

    selectAllColumns() {
      this.visibleColumns = this.columnOptions.map((col) => col.value);
    },

    clearAllColumns() {
      this.visibleColumns = [];
    },

    resetDefaultColumns() {
      this.visibleColumns = [...this.defaultColumns];
    },

    removeColumn(columnText) {
      const column = this.columnOptions.find((col) => col.text === columnText);
      if (column) {
        this.toggleColumn(column.value);
      }
    },

    // Método para cores dos status
    getStatusColor(status) {
      const colors = {
        sale: "success",
        order: "primary",
        waiting_approval: "warning",
        proposal: "info",
        draft: "grey",
        canceled: "error",
      };

      return colors[status] || "grey";
    },
  },
};
</script>